import { Helmet } from "react-helmet-async";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { ROUTES } from "../components/consts/routes.consts";
import { ToastContainer } from "react-toastify";
import { Home } from "../pages/home/home";
import "react-toastify/dist/ReactToastify.css";

import { AuthLayout } from "../components/layouts/auth-layout/auth.layout";
import { UnauthLayout } from "../components/layouts/unauth-layout/unauth.layouts";
import { useEffect, useState, Suspense, lazy } from "react";
import axios from "axios";
import { ProtectedMetaAdsRoute } from "../components/protected-route/ProtectedMetaAdsRoute";
import { ProtectedMetaNewCampaignRoute } from "../components/protected-route/ProtectedMetaNewCampaignRoute";
import { ProtectedRestrictedAccessRoute } from "../components/protected-route/ProtectedRestrictedAccessRoute";
import MetaAdsBuilder from "../pages/dashboard/meta-ads-builder";
import MetaAdsWallet from "../pages/metaAds/meta-ads-wallet";
import MetaNewCampaign from "../pages/metaAds/create-campaings";
import CampaignList from "../pages/metaAds/campaings-list";
import MyMetaCampaign from "../pages/dashboard/my-meta-campaign";

// import MetaAdsLogin from "../pages/metaAds/meta-ads-login";
import CreateCampaign from "../pages/metaAds/create-campaings";
import MetaAdsTermsConditions from "../pages/metaAds/privacy-terms/privacy-terms";
import CircularProgress from "@mui/material/CircularProgress";

// Lazy load all routes for code splitting
const TopChart = lazy(() => import("../pages/home/top-curator-chart"));
const Dashboard = lazy(() => import("../pages/dashboard/dashboard").then(module => ({ default: module.Dashboard })));
const Callback = lazy(() => import("../pages/callback").then(module => ({ default: module.Callback })));
const SubmitSongPage = lazy(() => import("../pages/submit-pages/submit-a-song").then(module => ({ default: module.SubmitSongPage })));
const SubmitPlaylistPage = lazy(() => import("../pages/submit-pages/submit-a-playlist").then(module => ({ default: module.SubmitPlaylistPage })));
const TermsConditions = lazy(() => import("../pages/home/privacy-terms/privacy-terms"));
const CreateSmartLinkPage = lazy(() => import("../pages/smartLink/create-smartlink"));
const SmartLinksPage = lazy(() => import("../pages/smartLink/smart-links"));
const SmartLinkDetailsPage = lazy(
  () => import("../pages/smartLink/smart-link-details"),
);
const DashboardLayout = lazy(() => import("../pages/dashboard/dashboard-layout").then(module => ({ default: module.DashboardLayout })));
const ConfirmPlaylistPage = lazy(() => import("../pages/submit-pages/confirm-playlist").then(module => ({ default: module.ConfirmPlaylistPage })));
const SubmitASong = lazy(() => import("../pages/submit-pages/submit-a-song-1").then(module => ({ default: module.SubmitASong })));
const SelectingGenres = lazy(() => import("../pages/selecting-genres").then(module => ({ default: module.SelectingGenres })));
const VisitPublicPage = lazy(() => import("../pages/dashboard/visit-public-page"));
const ReferralPage = lazy(() => import("../pages/referral-page").then(module => ({ default: module.ReferralPage })));
const MetaAdsReferralPage = lazy(() => import("../pages/metaAds/meta-ads-referral-page").then(module => ({ default: module.default })));
const PageNotFound = lazy(() => import("../pages/submit-pages/page-not-found"));
const ArtistBannedPage = lazy(() => import("../pages/submit-pages/artist-banned").then(module => ({ default: module.ArtistBannedPage })));
const ProfileBannedPage = lazy(() => import("../pages/submit-pages/profile-banned").then(module => ({ default: module.ProfileBannedPage })));
const Accounts = lazy(() => import("../pages/profile/accounts").then(module => ({ default: module.Accounts })));
const FAQPage = lazy(() => import("../pages/faq-page"));
const Unsubscribe = lazy(() => import("../pages/unsubscribe"));
const UnsubscribeNewsletterEmail = lazy(() => import("../pages/unsubscribe-email"));
const VerifyEmail = lazy(() => import("../pages/verify-email"));
const Reviews = lazy(() => import("../pages/reviews"));
const Contact = lazy(() => import("../pages/Contact-Us"));
const Sitemap = lazy(() => import("../pages/sitemap"));
const PlaylistConnectPopup = lazy(() => import("../components/modals/PlatlistConnectPopup"));
const TrackConnectPopup = lazy(() => import("../components/modals/TrackConnectPopup"));
const CuratorPublicPage = lazy(() => import("../pages/curator-public-page"));
const CuratorSongInfoPage = lazy(() => import("../pages/curator-song-info-page"));
const CuratorAIConfirmationPage = lazy(() => import("../pages/curator-ai-confirmation-page"));
const CuratorAIRejectionPage = lazy(() => import("../pages/curator-ai-rejection-page"));
const CuratorPlaylistSelectPage = lazy(() => import("../pages/curator-playlist-select-page"));
const SmartLinkPublic = lazy(() => import("../pages/smartLink/smart-link-public"));
const MetaAdsBuilderPublic = lazy(() => import("../pages/metaAds/meta-ads-builder-public"));

// Loading fallback component
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen w-full bg-black">
    <CircularProgress
      size={40}
      sx={{
        color: "#ffffff",
        display: "inline-flex",
        alignItems: "center",
        fontWeight: 900,
      }}
    />
  </div>
);

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

// Map routes → API keys + fallback meta
const SEO_MAP: Record<
  string,
  { key: string; defaultTitle: string; defaultDesc: string; canonical: string }
> = {
  [ROUTES.HOME]: {
    key: "home",
    defaultTitle: "Pitchplaylists",
    defaultDesc:
      "Get noticed and promote Spotify playlist with Pitchplaylists! A platform where indie artists can submit songs to Spotify playlists for free and boost their Spotify promotion.",
    canonical: "https://pitchplaylists.com/",
  },
  [ROUTES.FAQ]: {
    key: "faq",
    defaultTitle: "Faq",
    defaultDesc:
      "Get answers to frequently asked questions about artist submissions, playlist curator listings, and service features on Pitchplaylists in this helpful FAQ.",
    canonical: "https://pitchplaylists.com/faq",
  },
  [ROUTES.REVIEWS]: {
    key: "reviews",
    defaultTitle: "Pitchplaylists Reviews | Artist & Curator Feedback",
    defaultDesc:
      "Our reviews of Pitchplaylists from independent artists and curators. Discover real experiences of music promotion and playlist submissions on our trusted platform.",
    canonical: "https://pitchplaylists.com/reviews",
  },
  [ROUTES.CONTACT]: {
    key: "contact_us",
    defaultTitle: "Contact Pitchplaylists | Artist & Curator Support",
    defaultDesc:
      "Connect with the Pitchplaylists team for support, inquiries, or collaboration opportunities. Whether you're an artist or curator, we're here to help your music journey.",
    canonical: "https://pitchplaylists.com/contact-us",
  },
  [ROUTES.SITEMAP]: {
    key: "sitemap",
    defaultTitle: "Sitemap",
    defaultDesc:
      "Use the Pitchplaylists sitemap to explore site structure and access key pages, tools, and features quickly for artists and playlist curators.",
    canonical: "https://pitchplaylists.com/sitemap",
  },
  [ROUTES.TERMS_AND_CONDITIONS]: {
    key: "terms_and_conditions",
    defaultTitle: "Terms and Conditions",
    defaultDesc:
      "Review Pitchplaylists' terms and conditions to understand data usage, service policies, and user responsibilities when submitting music or collaborating with curators.",
    canonical: "https://pitchplaylists.com/terms-and-conditions",
  },
  [ROUTES.META_PRIVACY_TERMS]: {
    key: "meta_ads_terms_and_conditions",
    defaultTitle: "Meta Ads Terms and Conditions",
    defaultDesc:
      "Review Pitchplaylists' Meta Ads terms and conditions to understand data usage, service policies, and user responsibilities when using Meta Ads features.",
    canonical: "https://pitchplaylists.com/meta-ads/terms-and-conditions",
  },
};

export const RootNavigator = () => {
  const query = useQuery();
  const accessToken: any = query.get("access_token");
  const refreshToken: any = query.get("refresh_token");
  const curatorName: any = query.get("curator");
  const trackId: any = query.get("trackId");
  const referrerId: any = query.get("referrerId");
  const playlistId: any = query.get("playlistId");
  const page: any = query.get("page");

  if (accessToken) {
    window.localStorage.setItem("token", accessToken);
  }
  if (refreshToken) {
    window.localStorage.setItem("refresh_token", refreshToken);
  }
  const userId = localStorage.getItem("userId");
  const token = localStorage.getItem("token");
  const isLoggedIn = token && userId;

  const location = useLocation();
  const [routesData, setRoutesData] = useState<any>({});

  // Add/remove body class for styling per page
  useEffect(() => {
    if (location.pathname !== "/") {
      document.body.classList.add(
        location.pathname?.substring(1)?.split("/")[0],
      );

      return () => {
        document.body.classList.remove(
          location.pathname?.substring(1)?.split("/")[0],
        );
      };
    } else {
      document.body.classList.add("home-page");
      return () => {
        document.body.classList.remove("home-page");
      };
    }
  }, [location]);

  // Blog redirect
  const BlogRedirect = () => {
    window.location.href = "https://pitchplaylists.com/blog/";
    return null;
  };

  // Fetch SEO from WP ACF on route change
  useEffect(() => {
    const fetchApi = async () => {
      try {
        const response = await axios.get(
          "https://pitchplaylists.com/blog/wp-json/acf/v3/pages/288",
        );
        setRoutesData(response.data.acf || {});
      } catch (error) {
        console.log("SEO fetch error:", error);
      }
    };
    fetchApi();
  }, [location.pathname]);

  // Select SEO data for current route
  const seoConfig = SEO_MAP[location.pathname];
  const seoData = seoConfig
    ? {
        title: routesData?.[seoConfig.key]?.title || seoConfig.defaultTitle,
        description:
          routesData?.[seoConfig.key]?.description || seoConfig.defaultDesc,
        canonical: seoConfig.canonical,
      }
    : null;

  return (
    <>
      {seoData && (
        <Helmet>
          <meta charSet="utf-8" />
          <title>{seoData.title || "Pitchplaylists"}</title>
          <meta
            name="description"
            content={
              seoData.description ||
              "Default meta description for Pitchplaylists"
            }
          />
          <link
            rel="canonical"
            href={seoData.canonical || "https://pitchplaylists.com/"}
          />

          <meta
            property="og:title"
            content={seoData.title || "Pitchplaylists"}
          />
          <meta
            property="og:description"
            content={
              seoData.description ||
              "Default meta description for Pitchplaylists"
            }
          />
          <meta
            property="og:url"
            content={seoData.canonical || "https://pitchplaylists.com/"}
          />
          <meta property="og:type" content="website" />
          <meta
            property="og:image"
            content={"https://pitchplaylists.com/newsletter-images/logo.png"}
          />

          <meta name="twitter:card" content="summary_large_image" />
          <meta
            name="twitter:title"
            content={seoData.title || "Pitchplaylists"}
          />
          <meta
            name="twitter:description"
            content={
              seoData.description ||
              "Default meta description for Pitchplaylists"
            }
          />
          <meta
            name="twitter:image"
            content={"https://pitchplaylists.com/newsletter-images/logo.png"}
          />
        </Helmet>
      )}

      <Routes>
        <Route path={ROUTES.REFERRAL_PAGE} element={<Suspense fallback={<LoadingFallback />}><ReferralPage /></Suspense>} />
        <Route path={ROUTES.META_ADS_REFERRAL} element={<Suspense fallback={<LoadingFallback />}><MetaAdsReferralPage /></Suspense>} />
        <Route path={ROUTES.CURATOR_PUBLIC_PAGE} element={<Suspense fallback={<LoadingFallback />}><CuratorPublicPage /></Suspense>} />
        <Route path={ROUTES.CURATOR_SONG_INFO} element={<Suspense fallback={<LoadingFallback />}><CuratorSongInfoPage /></Suspense>} />
        <Route path={ROUTES.CURATOR_AI_CONFIRMATION} element={<Suspense fallback={<LoadingFallback />}><CuratorAIConfirmationPage /></Suspense>} />
        <Route path={ROUTES.CURATOR_AI_REJECTION} element={<Suspense fallback={<LoadingFallback />}><CuratorAIRejectionPage /></Suspense>} />
        <Route path={ROUTES.CURATOR_PLAYLIST_SELECT} element={<Suspense fallback={<LoadingFallback />}><CuratorPlaylistSelectPage /></Suspense>} />

        <Route element={isLoggedIn ? <AuthLayout /> : <UnauthLayout />}>
          <Route
            path={ROUTES.HOME}
            element={
              !isLoggedIn ? (
                <Home />
              ) : (
                <Navigate to={ROUTES.DASHBOARD} replace={true} />
              )
            }
          />

          <Route path={ROUTES.FAQ} element={<Suspense fallback={<LoadingFallback />}><FAQPage /></Suspense>} />
          <Route path={ROUTES.REVIEWS} element={<Suspense fallback={<LoadingFallback />}><Reviews /></Suspense>} />
          <Route path={ROUTES.CONTACT} element={<Suspense fallback={<LoadingFallback />}><Contact /></Suspense>} />
          <Route path={ROUTES.SITEMAP} element={<Suspense fallback={<LoadingFallback />}><Sitemap /></Suspense>} />

          <Route
            path={ROUTES.VISIT_PUBLIC_PAGE}
            element={<Suspense fallback={<LoadingFallback />}><VisitPublicPage /></Suspense>}
          />

          <Route element={<Suspense fallback={<LoadingFallback />}><DashboardLayout /></Suspense>}>
            <Route
              path={ROUTES.DASHBOARD}
              element={
                isLoggedIn ? (
                  <Suspense fallback={<LoadingFallback />}><Dashboard /></Suspense>
                ) : (
                  <Navigate to={ROUTES.HOME} replace={true} />
                )
              }
            />
            <Route
              path={ROUTES.SUBMIT_A_SONG}
              element={
                isLoggedIn ? (
                  <Suspense fallback={<LoadingFallback />}><SubmitSongPage /></Suspense>
                ) : (
                  <Navigate
                    to={ROUTES.HOME}
                    replace={true}
                    state={{
                      isShowConnectPopup: true,
                      isSongSubmitRedirect: true,
                      routeRedirectPage: "SUBMIT-A-SONG",
                    }}
                  />
                )
              }
            />
            <Route
              path={ROUTES.SETTINGS}
              element={
                isLoggedIn ? (
                  <Suspense fallback={<LoadingFallback />}><Accounts /></Suspense>
                ) : (
                  <Navigate to={ROUTES.HOME} replace={true} />
                )
              }
            />
            <Route
              path={ROUTES.SUBMIT_A_SONG_1}
              element={
                isLoggedIn ? (
                  <Suspense fallback={<LoadingFallback />}><SubmitASong /></Suspense>
                ) : (
                  <Navigate to={ROUTES.TRACK} replace={true} />
                )
              }
            />
            <Route
              path={ROUTES.ARTIST_BANNED}
              element={
                isLoggedIn ? (
                  <Suspense fallback={<LoadingFallback />}><ArtistBannedPage /></Suspense>
                ) : (
                  <Navigate to={ROUTES.HOME} replace={true} />
                )
              }
            />
            <Route
              path={ROUTES.SUBMIT_A_PLAYLIST}
              element={
                isLoggedIn ? (
                  <Suspense fallback={<LoadingFallback />}><SubmitPlaylistPage /></Suspense>
                ) : (
                  <Navigate
                    to={ROUTES.HOME}
                    replace={true}
                    state={{
                      isShowConnectPopup: true,
                      isSongSubmitRedirect: false,
                      routeRedirectPage: "SUBMIT-A-PLAYLIST",
                    }}
                  />
                )
              }
            />
            <Route
              path={ROUTES.CONFIRM_PLAYLIST}
              element={
                isLoggedIn ? (
                  <Suspense fallback={<LoadingFallback />}><ConfirmPlaylistPage /></Suspense>
                ) : (
                  <Navigate to={ROUTES.HOME} replace={true} />
                )
              }
            />
            <Route
              path={ROUTES.TOP_CURATOR_CHART}
              element={
                isLoggedIn ? (
                  <Suspense fallback={<LoadingFallback />}><TopChart /></Suspense>
                ) : (
                  <Navigate to={ROUTES.HOME} replace={true} />
                )
              }
            />
            <Route
              path={ROUTES.CREATE_SMART_LINK}
              element={
                isLoggedIn ? (
                  <ProtectedRestrictedAccessRoute>
                    <Suspense fallback={<LoadingFallback />}><CreateSmartLinkPage /></Suspense>
                  </ProtectedRestrictedAccessRoute>
                ) : (
                  <Navigate to={ROUTES.HOME} replace={true} />
                )
              }
            />
            <Route
              path={ROUTES.SMART_LINKS}
              element={
                isLoggedIn ? (
                  <ProtectedRestrictedAccessRoute>
                    <Suspense fallback={<LoadingFallback />}><SmartLinksPage /></Suspense>
                  </ProtectedRestrictedAccessRoute>
                ) : (
                  <Navigate to={ROUTES.HOME} replace={true} />
                )
              }
            />
            <Route
              path={ROUTES.SMART_LINK_DETAILS}
              element={
                isLoggedIn ? (
                  <ProtectedRestrictedAccessRoute>
                    <Suspense fallback={<LoadingFallback />}><SmartLinkDetailsPage /></Suspense>
                  </ProtectedRestrictedAccessRoute>
                ) : (
                  <Navigate to={ROUTES.HOME} replace={true} />
                )
              }
            />
            <Route
              path={ROUTES.META_ADS_BUILDER}
              element={
                isLoggedIn ? (
                  <Navigate
                    to={{
                      pathname: ROUTES.META_ADS_SETUP_PUBLIC,
                      search: location.search,
                    }}
                    state={location.state}
                    replace={true}
                  />
                ) : (
                  <Navigate to={ROUTES.HOME} replace={true} />
                )
              }
            />
            <Route
              path={ROUTES.META_ADS_SETUP_PUBLIC}
              element={
                isLoggedIn ? (
                  <ProtectedRestrictedAccessRoute>
                    <Suspense fallback={<LoadingFallback />}>
                      <MetaAdsBuilderPublic />
                    </Suspense>
                  </ProtectedRestrictedAccessRoute>
                ) : (
                  <Navigate to={ROUTES.HOME} replace={true} />
                )
              }
            />
            <Route
              path={ROUTES.META_ADS_WALLET}
              element={
                isLoggedIn ? (
                  <MetaAdsWallet />
                ) : (
                  <Navigate to={ROUTES.HOME} replace={true} />
                )
              }
            />
            <Route
              path={ROUTES.META_NEW_COMPAING_PUBLIC}
              element={
                isLoggedIn ? (
                  <ProtectedRestrictedAccessRoute>
                    <CreateCampaign />
                  </ProtectedRestrictedAccessRoute>
                ) : (
                  <Navigate to={ROUTES.HOME} replace={true} />
                )
              }
            />
            <Route
              path={ROUTES.MY_META_CAMPAIGN_PUBLIC}
              element={
                isLoggedIn ? (
                  <ProtectedRestrictedAccessRoute>
                    <CampaignList />
                  </ProtectedRestrictedAccessRoute>
                ) : (
                  <Navigate to={ROUTES.HOME} replace={true} />
                )
              }
            />
          </Route>

          <Route
            path={ROUTES.META_NEW_COMPAING}
            element={
              isLoggedIn ? (
                <ProtectedMetaNewCampaignRoute />
              ) : (
                <Navigate to={ROUTES.HOME} replace={true} />
              )
            }
          />

          <Route
            path={ROUTES.MY_META_CAMPAIGN}
            element={
              isLoggedIn ? (
                <MyMetaCampaign />
              ) : (
                <Navigate to={ROUTES.HOME} replace={true} />
              )
            }
          />

          <Route
            path={ROUTES.SUBMIT_A_SONG_PLAYLIST}
            element={
              isLoggedIn ? (
                <Suspense fallback={<LoadingFallback />}><SelectingGenres /></Suspense>
              ) : (
                <Navigate to={ROUTES.HOME} replace={true} />
              )
            }
          />

          <Route
            path={ROUTES.TERMS_AND_CONDITIONS}
            element={<Suspense fallback={<LoadingFallback />}><TermsConditions /></Suspense>}
          />
        </Route>

        <Route
          path={ROUTES.CALLBACK}
          element={
            token ? (
              <Suspense fallback={<LoadingFallback />}>
                <Callback
                  trackId={trackId}
                  playlistId={playlistId}
                  curatorName={curatorName}
                  referrerId={referrerId}
                  page={page}
                />
              </Suspense>
            ) : (
              <Navigate to={ROUTES.HOME} replace={true} />
            )
          }
        />

        <Route
          path={ROUTES.PROFILE_BANNED}
          element={<Suspense fallback={<LoadingFallback />}><ProfileBannedPage /></Suspense>}
        />

        <Route path={ROUTES.SMART_LINK_PUBLIC} element={<Suspense fallback={<LoadingFallback />}><SmartLinkPublic /></Suspense>} />
        <Route path={ROUTES.SMART_LINK_PUBLIC_SINGLE} element={<Suspense fallback={<LoadingFallback />}><SmartLinkPublic /></Suspense>} />
        <Route path={ROUTES.SMART_LINK_PUBLIC_DOUBLE} element={<Suspense fallback={<LoadingFallback />}><SmartLinkPublic /></Suspense>} />
        <Route path="*" element={<Suspense fallback={<LoadingFallback />}><PageNotFound /></Suspense>} />
        <Route path="/unsubscribe" element={<Suspense fallback={<LoadingFallback />}><Unsubscribe /></Suspense>} />
        <Route path={ROUTES.VERIFY_EMAIL} element={<Suspense fallback={<LoadingFallback />}><VerifyEmail /></Suspense>} />
        <Route path="/blog" element={<BlogRedirect />} />
        <Route
          path="/unsubscribe-newsletter"
          element={<Suspense fallback={<LoadingFallback />}><UnsubscribeNewsletterEmail /></Suspense>}
        />
        <Route path="/submit-track" element={<Suspense fallback={<LoadingFallback />}><TrackConnectPopup /></Suspense>} />
        <Route path="/add-playlist" element={<Suspense fallback={<LoadingFallback />}><PlaylistConnectPopup /></Suspense>} />

        <Route
          path={ROUTES.META_PRIVACY_TERMS}
          element={
              <MetaAdsTermsConditions />
          }
        />
        {/* <Route path={ROUTES.META_ADS_LOGIN} element={<MetaAdsLogin />} /> */}
      </Routes>

      <ToastContainer position="bottom-right" theme="colored" />
    </>
  );
};
